import React, { useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { LockKey, X, Eye, EyeSlash, Spinner } from '@phosphor-icons/react';

/**
 * ChangePasswordModal — lets a logged-in user change their password by
 * confirming the current one. Each field has a show/hide toggle. On success the
 * session is dropped and the user is sent to the login page to sign in again.
 *
 * Pass an `accent` prop (e.g. "#10b981" startup green, "#6366f1" VC indigo).
 */
const PasswordField = ({ label, value, onChange, placeholder, testId, autoFocus, accent }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          data-testid={testId}
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 bg-background border border-border rounded-lg text-[13px] text-text-primary focus:outline-none"
          style={{ borderColor: undefined }}
          onFocus={(e) => { e.target.style.borderColor = accent; }}
          onBlur={(e) => { e.target.style.borderColor = ''; }}
          autoComplete="off"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow((s) => !s)}
          title={show ? 'Hide' : 'Show'}
          aria-label={show ? 'Hide password' : 'Show password'}
          data-testid={`${testId}-toggle`}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-text-muted hover:text-text-primary"
        >
          {show ? <EyeSlash size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
};

const ChangePasswordModal = ({ onClose, accent = '#10b981' }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!current || !next || !confirm) {
      toast.error('Please fill in all fields');
      return;
    }
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (next === current) {
      toast.error('New password must be different from the current one');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password changed. Please log in again.');
      // Drop the session and force a fresh login with the new password.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setTimeout(() => { window.location.href = '/login'; }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="change-password-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-surface rounded-xl border border-border w-full max-w-md p-6"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <LockKey size={18} weight="bold" style={{ color: accent }} />
            <h2 className="text-lg font-heading font-bold text-text-primary">Change password</h2>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary" data-testid="change-password-close">
            <X size={18} />
          </button>
        </div>

        <p className="text-[12px] text-text-muted mb-5">
          Enter your current password and choose a new one. You'll be signed out and asked to log in again.
        </p>

        <div className="space-y-4">
          <PasswordField
            label="Current password"
            value={current}
            onChange={setCurrent}
            placeholder="Your current password"
            testId="current-password-input"
            autoFocus
            accent={accent}
          />
          <PasswordField
            label="New password"
            value={next}
            onChange={setNext}
            placeholder="At least 8 characters"
            testId="new-password-input"
            accent={accent}
          />
          <PasswordField
            label="Confirm new password"
            value={confirm}
            onChange={setConfirm}
            placeholder="Re-enter new password"
            testId="confirm-password-input"
            accent={accent}
          />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-[13px] font-medium text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="change-password-submit"
            className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: accent }}
          >
            {saving ? (
              <>
                <Spinner size={14} className="animate-spin" /> Changing…
              </>
            ) : (
              'Change password'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordModal;
